import React, { Component } from 'react';
import styled from 'styled-components';

import { debounce } from '../../utils/debounce';
import { RegularText, BoldText } from '../common/Text';
import LoadingComponent from '../common/LoadingComponent';

/*
 * Reusable search-select styled after the "By Doctor" search (DoctorSearch.js):
 * a text input plus a list of selectable rows, backed by a debounced async loader.
 * Unlike a plain dropdown it fits inline (the results render in a collapsing overlay),
 * so it can be used both full-width (By Pool / By Sponsor) and in field rows
 * (Copy Doctors / Manage Pools).
 *
 * Props:
 *  - loadOptions(term): Promise<Array<{ value, label, ... }>>  (term '' → initial list)
 *  - onSelect(option|null): called when a row is picked or the selection is cleared
 *  - selected: the currently-selected option (for highlight + closed-state label)
 *  - placeholder: input placeholder
 *  - width: optional CSS width for the wrapper (defaults to a sensible min-width)
 */

const Wrapper = styled.div`
  position: relative;
  min-width: 22rem;
  ${(props) => (props.width ? `width: ${props.width};` : '')}
`;

const InputRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  font-family: BrandonText-Medium, Lato, sans-serif;
  width: 100%;
  padding: 11px 34px 11px 14px;
  border: 1px solid #ddd6ee;
  border-radius: 10px;
  font-size: 0.9rem;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  border: none;
  background: transparent;
  color: #b0a7cf;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  :hover {
    color: #6b4bd6;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  margin-top: 6px;
  max-height: 20rem;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #eceaf3;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(75, 46, 131, 0.14);
  padding: 6px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  margin-top: 4px;
  background-color: ${(props) => (props.selected ? '#f0e9fa' : '#fafafa')};
  border: 0.1px #cecece solid;
  border-radius: 6px;
  cursor: pointer;
  :first-child {
    margin-top: 0;
  }
  :hover {
    background-color: #f0f0f0;
  }
`;

const HelperText = styled(RegularText)`
  color: #888;
  padding: 8px;
`;

const LoadingWrap = styled.div`
  padding: 8px;
`;

class SearchSelect extends Component {
  constructor(props) {
    super(props);
    this.state = {
      query: '',
      options: [],
      loading: false,
      open: false,
      searched: false,
    };
    // 400ms debounce, mirroring the existing search delay. One timer per instance,
    // so sibling pickers never cancel each other's in-flight load.
    this.debouncedLoad = debounce(this.runLoad, 400);
    this.wrapperRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideClick);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
  }

  componentDidUpdate(prevProps) {
    // Keep the input text in sync when the parent changes/clears the selection
    // (e.g. switching Pool Manager tabs resets the picker).
    if (prevProps.selected !== this.props.selected) {
      this.setState({
        query: this.props.selected ? this.props.selected.label : '',
      });
    }
  }

  handleOutsideClick = (e) => {
    if (
      this.wrapperRef.current &&
      !this.wrapperRef.current.contains(e.target)
    ) {
      this.setState({ open: false });
    }
  };

  runLoad = (term) => {
    this.setState({ loading: true });
    Promise.resolve(this.props.loadOptions(term))
      .then((options) => {
        this.setState({ options: options || [], searched: true });
      })
      .catch(() => {
        this.setState({ options: [], searched: true });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  onFocus = () => {
    const { selected } = this.props;
    const { query } = this.state;
    // If the field just shows the selected label (no fresh typing), load the initial list.
    const term = selected && query === selected.label ? '' : query;
    this.setState({ open: true });
    this.debouncedLoad(term);
  };

  onChange = (e) => {
    const query = e.target.value;
    this.setState({ query, open: true });
    this.debouncedLoad(query);
  };

  onPick = (option) => {
    this.setState({ query: option.label, open: false });
    this.props.onSelect(option);
  };

  onClear = () => {
    this.setState({ query: '', options: [], searched: false, open: false });
    this.props.onSelect(null);
  };

  render() {
    const { query, options, loading, open, searched } = this.state;
    const { placeholder, selected, width } = this.props;
    const inputValue = open ? query : selected ? selected.label : query;

    return (
      <Wrapper ref={this.wrapperRef} width={width}>
        <InputRow>
          <SearchInput
            value={inputValue}
            placeholder={placeholder}
            onFocus={this.onFocus}
            onChange={this.onChange}
          />
          {selected ? (
            <ClearButton type="button" onClick={this.onClear}>
              ×
            </ClearButton>
          ) : null}
        </InputRow>
        {open ? (
          <Dropdown>
            {loading ? (
              <LoadingWrap>
                <LoadingComponent />
              </LoadingWrap>
            ) : (
              <React.Fragment>
                {options.map((option) => (
                  <Row
                    key={option.value}
                    selected={
                      selected && `${selected.value}` === `${option.value}`
                    }
                    onClick={() => this.onPick(option)}
                  >
                    <BoldText>{option.label}</BoldText>
                  </Row>
                ))}
                {searched && !options.length ? (
                  <HelperText>No results found.</HelperText>
                ) : null}
              </React.Fragment>
            )}
          </Dropdown>
        ) : null}
      </Wrapper>
    );
  }
}

export default SearchSelect;
